import { Signal, computed, inject, Service } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IsActiveMatchOptions, Router, UrlTree } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { FoodbackAccountContext } from '@core/classes/account/account-context.class';
import { AccountService } from '@core/providers/account/account.service';
import { RolesService } from '@core/providers/backend/roles.service';
import { RouteBuilderService } from '@core/providers/backend/route-builder.service';
import { ContextService } from '@core/providers/context/context.service';
import { ContextUtil } from '@core/utils';
import { ACCOUNT_ROUTES_PATHS_FULL } from '@features/account/account-routes-paths';
import { ACCOUNT_SETTINGS_ROUTES_PATHS_FULL } from '@features/account/settings/acc-settings-routes-paths';
import { VENUE_SETTINGS_ROUTES_PATHS_FULL } from '@features/venue/settings/ven-settings-routes-paths';
import { VENUE_ROUTES_PATHS_FULL } from '@features/venue/venue-routes-paths';
import { isNavigationEnd } from '@layout/global-header/global-header.utils';
import { CONSTANTS } from '@shared/constants';
import { type ContextObject } from '@shared/interfaces/login-object';
import { type SidebarMenu } from '@shared/interfaces/sidebar/sidebar-menu.interface';
import { SidebarConstants } from '../sidebar/sidebar.constants';
import { SidebarService } from '../sidebar/sidebar.service';
import { navGroupIcon } from './nav-group-icons';
import {
  NAV_PATH_SEPARATOR,
  type NavAction,
  type NavContextInput,
  type NavDestination,
  type NavGroup,
  type NavItem,
  type NavSection,
} from './nav-model.interface';

const VENUES_TITLE_KEY = 'SHARED.SYSTEM.VENUES';

/**
 * Per-destination icons, keyed by nav title. Used where a destination is shown on its own
 * rather than inside its menu — the search palette — so each row is recognisable at a glance
 * instead of repeating one generic glyph.
 */
const DESTINATION_ICONS: Readonly<Record<string, string>> = {
  'SHARED.SYSTEM.COPILOT': 'auto_awesome',
  'SHARED.SYSTEM.PULSE_DASHBOARD': 'monitor_heart',
  'SHARED.SYSTEM.ALL_FOODBACKS': 'forum',
  'SHARED.SYSTEM.PULSE_SCOREBOARD': 'table_chart',
  'SHARED.SYSTEM.ONE_MORE_QUESTION': 'live_help',
  'SHARED.SYSTEM.CAMPAIGNS': 'campaign',
  'SHARED.SYSTEM.CONSUMER_INSIGHT': 'groups',
  'SHARED.SYSTEM.REPORT_ARCHIVE': 'archive',
  'SHARED.SYSTEM.PULSE_QUESTIONS': 'tune',
  'SHARED.SYSTEM.DETAILS': 'info',
  'SHARED.SYSTEM.VENUES': 'store',
  'SHARED.SYSTEM.GROUPS': 'folder_copy',
  'SIDEBAR.MENU.USER_MANAGEMENT': 'group',
  'SHARED.SYSTEM.REWARDS': 'emoji_events',
  'SHARED.SYSTEM.ADVERTISEMENT': 'ads_click',
  'SHARED.SYSTEM.GOALS': 'flag',
  'SHARED.SYSTEM.QR_URLS': 'qr_code_2',
  'SHARED.SYSTEM.QR_LANDING': 'qr_code_scanner',
  'SHARED.SYSTEM.LIVE_NOTIFICATIONS': 'notifications_active',
  'SHARED.SYSTEM.FLEXIBLE_PORTAL': 'web_asset',
  'SHARED.SYSTEM.BRANDING': 'palette',
  'SHARED.SYSTEM.REGIONAL_SETTINGS': 'language',
  'SHARED.SYSTEM.CONFIGURATION': 'settings',
  'SHARED.SYSTEM.UNITS': 'store',
};

/** Anything unmapped still gets a page-shaped icon rather than a bare arrow. */
const DESTINATION_ICON_FALLBACK = 'description';

/**
 * Each group's own page, which lists the group's destinations as cards. Giving a group a link is
 * what makes the rail item navigable: a pop-out is a pointer affordance, so without a page behind
 * it a keyboard or touch user had no way to see what a section contained.
 */
const SECTION_LANDING_LINKS: Readonly<Record<string, { account: string; venue: string }>> = {
  analytics: {
    account: ACCOUNT_ROUTES_PATHS_FULL.SECTIONS.ANALYTICS,
    venue: VENUE_ROUTES_PATHS_FULL.SECTIONS.ANALYTICS,
  },
  surveys: {
    account: ACCOUNT_ROUTES_PATHS_FULL.SECTIONS.SURVEYS,
    venue: VENUE_ROUTES_PATHS_FULL.SECTIONS.SURVEYS,
  },
  features: {
    account: ACCOUNT_ROUTES_PATHS_FULL.SECTIONS.FEATURES,
    venue: VENUE_ROUTES_PATHS_FULL.SECTIONS.FEATURES,
  },
  settings: {
    account: ACCOUNT_ROUTES_PATHS_FULL.SECTIONS.SETTINGS,
    venue: VENUE_ROUTES_PATHS_FULL.SECTIONS.SETTINGS,
  },
};

/** Admin sits outside any account or venue, so its landing needs no context substitution. */
const ADMIN_LANDING_LINK = `/${CONSTANTS.ROUTES.ADMIN}`;

/**
 * Each context's entry point. A venue has no scoreboard of its own, so it lands on its Pulse
 * dashboard — the same pages `LoginGuard` resolves to once a session is established.
 */
const HOME_LINKS: Readonly<{ account: string; venue: string }> = {
  account: ACCOUNT_ROUTES_PATHS_FULL.SCOREBOARD,
  venue: VENUE_ROUTES_PATHS_FULL.DASHBOARD,
};

/** The blurb introducing each group at the top of its landing page. */
const SECTION_DESCRIPTIONS: Readonly<Record<string, string>> = {
  analytics: 'SHARED.NAV_LANDING.SECTIONS.ANALYTICS',
  surveys: 'SHARED.NAV_LANDING.SECTIONS.SURVEYS',
  features: 'SHARED.NAV_LANDING.SECTIONS.FEATURES',
  settings: 'SHARED.NAV_LANDING.SECTIONS.SETTINGS',
  admin: 'SHARED.NAV_LANDING.SECTIONS.ADMIN',
};

/**
 * Headings for the runs of cards a landing page shows, in order. Kept off `NavSection` because a
 * labelled section is a nested, indented heading in the rail's pop-out, where the same grouping
 * reads as a rule. `null` takes the name of the context the page is built for.
 */
const LANDING_RUN_LABELS: Readonly<Record<string, ReadonlyArray<string | null>>> = {
  analytics: ['SHARED.SYSTEM.PULSE', 'SHARED.NAV_LANDING.GROUPS.ANALYTICS_MORE'],
  surveys: ['SHARED.NAV_LANDING.GROUPS.SURVEYS_SETUP'],
  features: ['SHARED.NAV_LANDING.GROUPS.FEATURES_AVAILABLE'],
  settings: [null, 'SHARED.NAV_LANDING.GROUPS.SETTINGS_CONSUMER_EXPERIENCE'],
  admin: ['SHARED.NAV_LANDING.GROUPS.ADMIN'],
};

/**
 * The one page whose route tail differs between contexts while being the same destination.
 * Everything else in the two trees already shares its tail.
 */
const PAGE_KEY_ALIASES: Readonly<Record<string, string>> = {
  'settings/account-details': 'settings/details',
  'settings/venue-details': 'settings/details',
};

/**
 * Strips the `/account/<uuid>` or `/venue/<uuid>` prefix off a nav link, leaving the part that
 * identifies the page itself. `/account/abc/analytics/campaigns` and
 * `/venue/xyz/analytics/campaigns` both reduce to `analytics/campaigns`.
 */
function contextRelativePath(link: string): string {
  const segments = link.split('/').filter(Boolean);
  const [root] = segments;

  const path =
    root === CONSTANTS.ROUTES.ACCOUNT || root === CONSTANTS.ROUTES.VENUE
      ? // Drop the root and the uuid that follows it.
        segments.slice(2).join('/')
      : segments.join('/');

  return PAGE_KEY_ALIASES[path] ?? path;
}

/** One context's half of a quick action: the page it belongs to, and where it goes. */
interface ActionRoute {
  page: string;
  target: string;
}

interface ActionDefinition {
  id: string;
  title: string;
  icon: string;
  account?: ActionRoute;
  venue?: ActionRoute;
}

/**
 * Quick actions for the search palette. Each is offered only where the page it belongs to is in
 * the menu, so it never reaches further than that page's own access rules allow.
 */
const ACTIONS: ReadonlyArray<ActionDefinition> = [
  {
    id: 'invite-users',
    title: 'SHARED.USERS.INVITE_USERS',
    icon: 'person_add',
    account: {
      page: ACCOUNT_SETTINGS_ROUTES_PATHS_FULL.USERS.BASE_PATH,
      target: ACCOUNT_SETTINGS_ROUTES_PATHS_FULL.USERS.INVITE,
    },
    venue: {
      page: VENUE_SETTINGS_ROUTES_PATHS_FULL.USERS.BASE_PATH,
      target: VENUE_SETTINGS_ROUTES_PATHS_FULL.USERS.INVITE,
    },
  },
  {
    id: 'new-campaign',
    title: 'SHARED.CAMPAIGNS.NEW_CAMPAIGN',
    icon: 'add_circle',
    account: {
      page: ACCOUNT_SETTINGS_ROUTES_PATHS_FULL.CAMPAIGNS.BASE_PATH,
      target: ACCOUNT_SETTINGS_ROUTES_PATHS_FULL.CAMPAIGNS.CREATE,
    },
  },
  {
    id: 'add-question',
    title: 'SHARED.QUESTIONS.ADD_QUESTION',
    icon: 'add_comment',
    account: {
      page: ACCOUNT_SETTINGS_ROUTES_PATHS_FULL.ONE_MORE_QUESTION.BASE_PATH,
      target: ACCOUNT_SETTINGS_ROUTES_PATHS_FULL.ONE_MORE_QUESTION.CREATE,
    },
  },
  {
    id: 'add-unit',
    title: 'SHARED.VENUES.ADD_VENUE',
    icon: 'add_business',
    account: {
      page: ACCOUNT_SETTINGS_ROUTES_PATHS_FULL.VENUES.LIST,
      target: ACCOUNT_SETTINGS_ROUTES_PATHS_FULL.VENUES.CREATE,
    },
  },
  {
    id: 'new-notification',
    title: 'HOME.LIVE_NOTIFICATIONS.ADD.HEADERS.NEW_NOTIFICATION',
    icon: 'add_alert',
    venue: {
      page: VENUE_ROUTES_PATHS_FULL.NOTIFICATIONS.LIST,
      target: VENUE_ROUTES_PATHS_FULL.NOTIFICATIONS.ADD,
    },
  },
];

const ACTIVE_MATCH_OPTIONS: IsActiveMatchOptions = {
  paths: 'subset',
  queryParams: 'ignored',
  fragment: 'ignored',
  matrixParams: 'ignored',
};

@Service()
export class NavModelService {
  private readonly router = inject(Router);
  private readonly contextService = inject(ContextService);
  private readonly accountService = inject(AccountService);
  private readonly contextUtil = inject(ContextUtil);
  private readonly sidebarService = inject(SidebarService);
  private readonly rolesService = inject(RolesService);
  private readonly routeBuilderService = inject(RouteBuilderService);

  private readonly context = toSignal<ContextObject | null>(
    this.contextService.getContext$().pipe(startWith(null)),
    { initialValue: null }
  );

  private readonly account = toSignal<FoodbackAccountContext | null>(
    this.accountService.getAccountContext$().pipe(startWith(null)),
    { initialValue: null }
  );

  /** Bumped on every completed navigation so the active-state computeds re-evaluate. */
  private readonly navigationCount = toSignal(
    this.router.events.pipe(
      filter(isNavigationEnd),
      map((_event, index) => index + 1),
      startWith(0)
    ),
    { initialValue: 0 }
  );

  /**
   * Whether the current route sits outside any account or venue. The top-level dashboard reports
   * across all of them, so a single context's menu does not apply while you are there. The stored
   * context itself survives — returning to it is one click in the switcher.
   */
  readonly isOutOfContext: Signal<boolean> = computed(() => {
    this.navigationCount();

    return this.isActiveLink(`/${CONSTANTS.ROUTES.TOP_LEVEL_DASHBOARD}`);
  });

  /** The current context's own menu, whether or not the route is currently inside it. */
  private readonly contextGroups: Signal<ReadonlyArray<NavGroup>> = computed(() => {
    const context = this.context();
    const contextType = context?.type;
    const contextUuid = context?.uuid;

    if (!contextType || !contextUuid) {
      return [];
    }

    const isAccountContext = this.contextUtil.isAccountContext(contextType);

    return this.groupsFor({
      isAccountContext,
      contextUuid,
      canSeeSettings: isAccountContext
        ? context.isUserAccountAdmin === true
        : context.isUserVenueAdmin === true,
      isAiEnabled: this.account()?.isAiEnabled === true,
    });
  });

  /**
   * What the rail shows. Out of context only the context-free groups remain — the rail's search,
   * profile menu and logo are separate from the group list and are unaffected.
   */
  readonly groups: Signal<ReadonlyArray<NavGroup>> = computed(() =>
    this.isOutOfContext() ? this.contextFreeGroups() : this.contextGroups()
  );

  /**
   * Where the logo goes. Not the app root: its guard resumes the stored last URL, which is the
   * page the user is already standing on. Without a context there is only the admin tool.
   */
  readonly homeLink: Signal<string> = computed(() => {
    const context = this.context();

    if (!context?.uuid) {
      return this.rolesService.isAdmin() ? ADMIN_LANDING_LINK : '/';
    }

    const isAccountContext =
      context.type !== undefined && this.contextUtil.isAccountContext(context.type);

    return (
      this.contextLink(
        isAccountContext ? HOME_LINKS.account : HOME_LINKS.venue,
        isAccountContext,
        context.uuid
      ) ?? '/'
    );
  });

  /**
   * Every destination in the current context, flattened with its menu path. Taken from the context's
   * own tree rather than from `groups`, so the search palette can still offer a way back into the
   * context from a page that sits outside it — following one of those links re-enters the context.
   */
  readonly destinations: Signal<ReadonlyArray<NavDestination>> = computed(() =>
    this.flattenDestinations(this.contextGroups())
  );

  /** The quick actions the current context offers. */
  readonly actions: Signal<ReadonlyArray<NavAction>> = computed(() => {
    const context = this.context();
    const contextUuid = context?.uuid;

    if (!context?.type || !contextUuid) {
      return [];
    }

    const isAccountContext = this.contextUtil.isAccountContext(context.type);
    const destinations = this.destinations();

    return ACTIONS.flatMap(action => {
      const route = isAccountContext ? action.account : action.venue;
      const pageLink = route && this.contextLink(route.page, isAccountContext, contextUuid);
      const page = pageLink && destinations.find(destination => destination.link === pageLink);
      const link = route && this.contextLink(route.target, isAccountContext, contextUuid);

      if (!page || !link) {
        return [];
      }

      return [
        {
          id: action.id,
          title: action.title,
          icon: action.icon,
          link,
          path: page.path ? `${page.path}${NAV_PATH_SEPARATOR}${page.title}` : page.title,
        },
      ];
    });
  });

  /**
   * The nav tree for a context that is not (yet) the current one. Used when switching, to ask
   * whether the destination the user is on exists in the account or venue they are moving to.
   */
  destinationsFor(input: NavContextInput): ReadonlyArray<NavDestination> {
    return this.flattenDestinations(this.groupsFor(input));
  }

  /**
   * Identity of the page the current URL belongs to, or null if the URL isn't a nav
   * destination. Matching is by URL prefix, so a detail route resolves to the list it lives
   * under — a campaign id means nothing in another account, but "campaigns" does.
   */
  activePageKey(): string | null {
    const matches = this.destinations()
      .filter(destination => this.isActiveLink(destination.link))
      // Longest link wins, so a nested destination beats its parent.
      .sort((first, second) => second.link.length - first.link.length);

    return matches.at(0)?.pageKey ?? null;
  }

  /**
   * The groups that mean anything without a context. Only Admin qualifies, and its divider goes:
   * it separates admin from the customer-facing groups, and there are none to separate it from.
   */
  private contextFreeGroups(): ReadonlyArray<NavGroup> {
    const admin = this.buildAdminGroup();

    return admin ? [{ ...admin, hasDividerBefore: false }] : [];
  }

  private groupsFor(input: NavContextInput): ReadonlyArray<NavGroup> {
    const { isAccountContext, contextUuid, canSeeSettings } = input;

    const nav = SidebarConstants.NAV.build(isAccountContext);
    this.renameVenuesToAccountChildren(nav.settings.submenu);

    const groups: NavGroup[] = [];

    if (input.isAiEnabled) {
      const copilot = this.buildDirectLinkGroup(
        'copilot',
        nav.copilot,
        isAccountContext,
        contextUuid,
        canSeeSettings
      );
      if (copilot) {
        groups.push(copilot);
      }
    }

    for (const [key, menu] of [
      ['analytics', nav.analytics],
      ['surveys', nav.surveys],
      ['features', nav.features],
      ['settings', nav.settings],
    ] as ReadonlyArray<[string, SidebarMenu]>) {
      groups.push(this.buildGroup(key, menu, isAccountContext, contextUuid, canSeeSettings));
    }

    const admin = this.buildAdminGroup();
    if (admin) {
      groups.push(admin);
    }

    // A group with nothing the user may reach is dropped rather than shown with an empty pop-out.
    return groups.filter(group => group.link || group.sections.length > 0);
  }

  /**
   * Every reachable page, each group's landing included. A landing is emitted alongside its
   * items, never in place of them — search needs both, and a context switch resolves against
   * this list to find the equivalent of the page you are on.
   */
  private flattenDestinations(groups: ReadonlyArray<NavGroup>): ReadonlyArray<NavDestination> {
    return groups.flatMap(group => {
      const landing: NavDestination[] = group.link
        ? [
            {
              title: group.title,
              link: group.link,
              path: '',
              pageKey: contextRelativePath(group.link),
              // Per-destination map first: CoPilot has no group icon but still needs a row glyph.
              icon: this.iconFor(group.title, navGroupIcon(group.key, group.icon)),
            },
          ]
        : [];

      const items = group.sections.flatMap(section =>
        section.items.map(item => ({
          title: item.title,
          link: item.link,
          path: section.label ? `${group.title}${NAV_PATH_SEPARATOR}${section.label}` : group.title,
          pageKey: contextRelativePath(item.link),
          icon: this.iconFor(item.title, item.icon),
          description: item.description,
          searchKeywords: item.searchKeywords,
        }))
      );

      return [...landing, ...items];
    });
  }

  readonly activeGroupKey: Signal<string | null> = computed(() => {
    this.navigationCount();

    for (const group of this.groups()) {
      if (this.isGroupActive(group)) {
        return group.key;
      }
    }

    return null;
  });

  isActiveLink(link: string | null | undefined): boolean {
    if (!link || link.trim().length === 0) {
      return false;
    }

    try {
      const tree: UrlTree = link.startsWith('/')
        ? this.router.parseUrl(link)
        : this.router.createUrlTree([link]);

      return this.router.isActive(tree, ACTIVE_MATCH_OPTIONS);
    } catch {
      return false;
    }
  }

  private iconFor(title: string, declared?: string): string {
    return DESTINATION_ICONS[title] ?? declared ?? DESTINATION_ICON_FALLBACK;
  }

  private landingLabels(key: string, isAccountContext: boolean): ReadonlyArray<string> {
    const contextLabel = isAccountContext ? 'SHARED.SYSTEM.ACCOUNT' : 'SHARED.SYSTEM.VENUE';

    return (LANDING_RUN_LABELS[key] ?? []).map(label => label ?? contextLabel);
  }

  private isGroupActive(group: NavGroup): boolean {
    if (group.link && this.isActiveLink(group.link)) {
      return true;
    }

    return group.sections.some(section => section.items.some(item => this.isActiveLink(item.link)));
  }

  /**
   * The Foodback-internal admin tool, as a normal rail destination rather than a mode the
   * whole shell switches into. Its routes carry their own guards; this only decides whether
   * the entry is offered. Last in the order — it is staff tooling, not customer navigation.
   */
  private buildAdminGroup(): NavGroup | null {
    if (!this.rolesService.isAdmin()) {
      return null;
    }

    const items = this.toItems(SidebarConstants.ADMIN.SIDEBAR);

    if (items.length === 0) {
      return null;
    }

    return {
      key: 'admin',
      title: 'SHARED.SYSTEM.ADMIN',
      icon: 'admin_panel_settings',
      description: SECTION_DESCRIPTIONS.admin,
      landingLabels: this.landingLabels('admin', false),
      link: ADMIN_LANDING_LINK,
      // Ruled off from the customer-facing groups above it.
      hasDividerBefore: true,
      sections: [{ items }],
    };
  }

  private buildDirectLinkGroup(
    key: string,
    menu: SidebarMenu,
    isAccountContext: boolean,
    contextUuid: string,
    canSeeSettings: boolean
  ): NavGroup | null {
    const [resolved] = this.sidebarService.setSidebarsURLS(
      [menu],
      isAccountContext,
      contextUuid,
      canSeeSettings
    );

    if (!resolved?.link) {
      return null;
    }

    return {
      key,
      title: resolved.title,
      icon: resolved.icon,
      customSvg: resolved.customSvg,
      link: resolved.link,
      sections: [],
    };
  }

  private buildGroup(
    key: string,
    menu: SidebarMenu,
    isAccountContext: boolean,
    contextUuid: string,
    canSeeSettings: boolean
  ): NavGroup {
    const resolved = this.sidebarService.setSidebarsURLS(
      menu.submenu,
      isAccountContext,
      contextUuid,
      canSeeSettings
    );

    const sections: NavSection[] = [];
    let looseItems: NavItem[] = [];

    const flushLooseItems = () => {
      if (looseItems.length > 0) {
        sections.push({ items: looseItems });
        looseItems = [];
      }
    };

    for (const entry of resolved) {
      // A divider is its own entry in the nav constants, with neither a link nor children.
      // Carry it as a flag on the item it follows rather than dropping it.
      if (entry.isDivider) {
        const previous = looseItems.at(-1) ?? sections.at(-1)?.items.at(-1);
        if (previous) {
          previous.hasDividerAfter = true;
        }
        continue;
      }

      if (entry.submenu?.length) {
        flushLooseItems();
        const items = this.toItems(entry.submenu);
        if (items.length > 0) {
          sections.push({ label: entry.title, items });
        }
        continue;
      }

      if (entry.link) {
        looseItems.push(this.toItem(entry, entry.link));
      }
    }

    flushLooseItems();

    // Whatever the constants say, a rule below the final item would hang off the menu.
    const lastItem = sections.at(-1)?.items.at(-1);
    if (lastItem) {
      lastItem.hasDividerAfter = false;
    }

    return {
      key,
      title: menu.title,
      icon: menu.icon,
      customSvg: menu.customSvg,
      description: SECTION_DESCRIPTIONS[key],
      landingLabels: this.landingLabels(key, isAccountContext),
      // No link on an empty group, so `groupsFor`'s filter still drops it.
      link:
        sections.length > 0
          ? this.sectionLandingLink(key, isAccountContext, contextUuid)
          : undefined,
      sections,
    };
  }

  /** Resolves a group's landing path for the context being built, uuid substituted in. */
  private sectionLandingLink(
    key: string,
    isAccountContext: boolean,
    contextUuid: string
  ): string | undefined {
    const links = SECTION_LANDING_LINKS[key];

    if (!links) {
      return undefined;
    }

    return this.contextLink(
      isAccountContext ? links.account : links.venue,
      isAccountContext,
      contextUuid
    );
  }

  /** Substitutes a context's uuid into a route pattern from the account or venue tree. */
  private contextLink(
    routePath: string,
    isAccountContext: boolean,
    contextUuid: string
  ): string | undefined {
    const parameter = isAccountContext
      ? CONSTANTS.ROUTE_PARAMS.ACCOUNT_ID
      : CONSTANTS.ROUTE_PARAMS.VENUE_ID;

    try {
      return this.routeBuilderService.getAbsoluteRoute(routePath, { [parameter]: contextUuid });
    } catch {
      return undefined;
    }
  }

  private toItems(menus: ReadonlyArray<SidebarMenu>): NavItem[] {
    return menus.flatMap(menu => (menu.link ? [this.toItem(menu, menu.link)] : []));
  }

  private toItem(menu: SidebarMenu, link: string): NavItem {
    return {
      title: menu.title,
      link,
      // Most entries carry only a `customSvg`, so an unresolved icon leaves every card identical.
      icon: this.iconFor(menu.title, menu.icon),
      customSvg: menu.customSvg,
      description: menu.descriptionKey,
      searchKeywords: [
        ...(menu.searchKeywordKeys ?? []),
        ...(this.rolesService.isAdmin() ? (menu.foodbackAdminSearchKeywordKeys ?? []) : []),
      ],
      hasDividerAfter: menu.isDivider,
    };
  }

  private renameVenuesToAccountChildren(menus: SidebarMenu[]): void {
    const childrenName = this.contextService.getAccountChildrenNameTranslation(false);

    for (const menu of menus) {
      if (menu.title === VENUES_TITLE_KEY) {
        // Icons are looked up by title, so this one has to be resolved before the title is
        // replaced with the backend's wording — afterwards it would never match.
        menu.icon = this.iconFor(menu.title, menu.icon);
        menu.title = childrenName;
      }
    }
  }
}
